import { ref } from 'vue';
import { defineStore } from 'pinia';
import useUISessionStore from '../ui-session';
import { ReportType, ReportTypeResponse, PayrollDatabase, PayrollDatabaseResponse, PayrollCompany, PayrollCompanyResponse, ReportConfig, ReportSettings, ReportResponse } from '@/interfaces/CBW/WorkforceAssistance';
import { GridColumnProps } from '@progress/kendo-vue-grid';
import { generateColumnHeadings } from '@/lib/grid-letter-spacing';
import useAxiosInstance, { AxiosInstanceResponse } from '@/composables/axiosInstance';
import dayjs from 'dayjs';
import { SortDescriptor } from '@progress/kendo-data-query';
export const ADD_CONFIG_FILE_URL = '/api/WorkforceAssistanceReport/ConfigFiles';
export const REMOVE_CONFIG_FILE_URL = '/api/WorkforceAssistanceReport/ConfigFiles';

interface State {
  reportConfigs: ReportConfig[];
  reportTypes: ReportType[];
  payrollDatabases: PayrollDatabase[];
  payrollCompanies: PayrollCompany[];
  reportSettings: ReportSettings;
  sort: SortDescriptor[];
  response: ReportResponse;
  exporting: boolean;
  hideExportDisclaimer: boolean;
}

const useHRAdminWorkforceAssistanceStore = defineStore({
  id: 'hr-admin-workforce-reports',
  state: () => ref<State>({
    reportConfigs: [],
    reportTypes: [],
    payrollDatabases: [],
    payrollCompanies: [],
    reportSettings: {
      reportTypeId: 1,
      payrollCompanyId: '',
      payrollDatabaseId: '',
      effectiveFromDate: undefined,
      effectiveToDate: undefined,
      page: 1,
      pageSize: 100,
    },
    sort: [],
    response: {
      total: 0,
      totalPages: 0,
      currentPage: 0,
      results: []
    },
    exporting: false,
    hideExportDisclaimer: false,
  }),
  actions: {
    async init() {
      await this.getReportTypes();
      await this.getPayrollDatabases();
    },
    async getReportTypes(): Promise<void> {
      try {
        const { data } = await useAxiosInstance.get<ReportTypeResponse>('/api/WorkforceAssistanceReport/Types');

        this.$state = {
          ...this.$state,
          reportTypes: data.results
        };
      } catch (error: any) {
        console.error(error);
      }
    },
    async getPayrollDatabases(): Promise<void> {
      try {
        const { data } = await useAxiosInstance.get<PayrollDatabaseResponse>('/api/Companies');
        this.$state = {
          ...this.$state,
          payrollDatabases: data.results
        };
      } catch (error: any) {
        console.error(error);
      }
    },
    async getPayrollCompanies(id: number): Promise<void> {
      try {
        const { data } = await useAxiosInstance.get<PayrollCompanyResponse>(`/api/PayerGroups/${id}`);
        this.$state = {
          ...this.$state,
          payrollCompanies: data.results
        };
      } catch (error: any) {
        console.error(error);
      }
    },
    async getReport(): Promise<ReportResponse> {
      let response: ReportResponse;
      try {
        const { data } = await useAxiosInstance.get<ReportResponse>(`/api/WorkforceAssistanceReport${this.reportQuery}`, {
          params: {
            sort: this.sort,
          }
        });

        response = {
          ...data,
          results: data.results
        }

      } catch (error: any) {
        console.error(error);
        response = {
          total: 0,
          totalPages: 0,
          currentPage: 0,
          results: []
        }
      }

      if (!this.$state.exporting) {
        this.$state.response = response;
      }
      return response;
    },
    async getReportConfigs(): Promise<void> {
      try {
        const { data } = await useAxiosInstance.get<ReportConfig[]>('/api/WorkforceAssistanceReport/ConfigFiles');
        this.$state = {
          ...this.$state,
          reportConfigs: data
        }
       
      } catch (error: any) {
        console.error(error);
      }
    },
    async insertReportConfig(file: File) : Promise<AxiosInstanceResponse | void> {
      try {
        const uiStore = useUISessionStore();

        const response = await useAxiosInstance.put<AxiosInstanceResponse>(ADD_CONFIG_FILE_URL, {
          createdByEmpCode: uiStore.user.empCode,
          file,
        }, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        return {
          success: true,
        };
      } catch (error: any) {
        const errors = error.response?.data?.errors?.Error;
        if (errors) {
          return {
            errors: errors.flatMap((e: string) => e.split('\r\n')),
          };
        }
        throw new Error(error);
      }
    },
  },
  getters: {
    reportQuery(state): string {
      const settings = state.exporting ? {
        ...state.reportSettings,
        page: 1,
        pageSize: 99999,
      } : state.reportSettings;
      const query = Object.entries(settings).filter(([_, value]) => {
        if (value === undefined || value === null || typeof value === 'string' && value === '') {
          return false
        }

        return true;
      }).map(([key, value]) => {
        if (typeof value === 'object') {
          return `${key}=${dayjs(value).format('YYYY-MM-D')}`;
        }
        return `${key}=${value}`;
      }).join('&');

      return `?${encodeURI(query)}`;
    },
    columnHeadings(state): GridColumnProps[] {
      return generateColumnHeadings(state.response.results, state.sort);
    },
    configColumnHeadings(state): GridColumnProps[] {
      return [
        {
          field: 'version',
          title: 'Version',
          width: 90,
        },
        {
          field: 'fileName',
          title: 'File Name',
        },
        {
          field: 'description',
          title: 'Description',
        },
        {
          field: 'createdByEmpCode',
          title: 'Created By',
          width: 130
        },
        {
          field: 'createdDate',
          title: 'Created on',
          cell: 'createdDateTemplate',
          width: 130
        },
      ]
    }
  },
  persist: {
    enabled: true,
    strategies: [
      { storage: localStorage, paths: ['hideExportDisclaimer'] },
    ],
  },
});

export default useHRAdminWorkforceAssistanceStore;
